import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
  Pagination,
  Snackbar,
  Avatar,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  EditNote as EditNoteIcon,
  Archive as ArchiveIcon,
  Help as HelpIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CloudUpload as CloudUploadIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  DragHandle as DragHandleIcon,
} from "@mui/icons-material";
import AuthorForm from "./AuthorForm";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { env } from "../../../utilities/function";
import { getCookie } from "../../../utilities/cookies";
import { GrFormView } from "react-icons/gr";
import { MdOutlineArticle } from "react-icons/md";
import { MdOutlineVisibilityOff } from "react-icons/md";
import MDEditor from "@uiw/react-md-editor";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { useLocation } from "react-router-dom";

const API_BASE_URL = env("SERVER");

// Blog Post Status Types
export const BLOG_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  ARCHIVED: "archived",
};

// Blog Categories
export const blogCategories = [
  "All",
  "Technology",
  "Business",
  "Marketing",
  "Product Updates",
  "Industry News",
  "Startup",
  "Productivity",
  "Tutorials",
  "Company News",
  "News",
];

// Generate slug from title
export function generateSlug(title) {
  return title
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// API Functions
const fetchBlogPosts = async (page = 1, limit = 10, filters = {}) => {
  const accessToken = getCookie("accessToken");

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...filters,
  });

  const response = await axios.get(`${API_BASE_URL}/api/blogs?${params}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

const createBlogPost = async (data) => {
  const accessToken = getCookie("accessToken");

  // Generate slug from title
  const slug = generateSlug(data.title);

  const cleanData = {
    title: data.title,
    slug,
    excerpt: data.excerpt,
    content: data.content,
    featuredImage: data.featuredImage,
    author: data.author,
    category: data.category,
    tags: data.tags,
    seoTitle: data.seoTitle,
    seoDescription: data.seoDescription,
    status: data.status,
  };

  return await axios.post(`${API_BASE_URL}/api/blogs`, cleanData, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const updateBlogPost = async (id, data) => {
  const accessToken = getCookie("accessToken");

  // Generate slug from title if title changed
  const slug = data.title ? generateSlug(data.title) : data.slug;

  const cleanData = {
    title: data.title,
    slug,
    excerpt: data.excerpt,
    content: data.content,
    featuredImage: data.featuredImage,
    author: data.author,
    category: data.category,
    tags: data.tags,
    seoTitle: data.seoTitle,
    seoDescription: data.seoDescription,
    status: data.status,
  };

  return await axios.put(`${API_BASE_URL}/api/blogs/${id}`, cleanData, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const deleteBlogPost = async (id) => {
  const accessToken = getCookie("accessToken");

  return await axios.delete(`${API_BASE_URL}/api/blogs/${id}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const updateBlogStatus = async (id, status) => {
  const accessToken = getCookie("accessToken");

  return await axios.patch(
    `${API_BASE_URL}/api/blogs/${id}/status`,
    { status },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

const reorderBlogs = async (blogIds) => {
  const accessToken = getCookie("accessToken");
  return await axios.put(
    `${API_BASE_URL}/api/blogs/reorder`,
    { blogIds },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

const fetchBlogStats = async () => {
  const accessToken = getCookie("accessToken");

  const response = await axios.get(`${API_BASE_URL}/api/blogs/stats`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

// Fetch available authors
const fetchAvailableAuthors = async () => {
  const accessToken = getCookie("accessToken");

  const response = await axios.get(`${API_BASE_URL}/api/authors`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

// Fetch all authors for management
const fetchAllAuthors = async () => {
  const accessToken = getCookie("accessToken");

  const response = await axios.get(`${API_BASE_URL}/api/authors`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

// Create new author
const createAuthor = async (authorData) => {
  const accessToken = getCookie("accessToken");

  const response = await axios.post(`${API_BASE_URL}/api/authors`, authorData, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

// Update author
const updateAuthor = async (id, authorData) => {
  const accessToken = getCookie("accessToken");

  const response = await axios.put(
    `${API_BASE_URL}/api/authors/${id}`,
    authorData,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

// Delete author
const deleteAuthor = async (id) => {
  const accessToken = getCookie("accessToken");

  const response = await axios.delete(`${API_BASE_URL}/api/authors/${id}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

// Blog Form Component
const BlogForm = ({ open, onClose, blogPost, onSubmit, isEditing }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    featuredImage: "",
    author: "", // Only store author ID
    category: "",
    tags: [],
    seoTitle: "",
    seoDescription: "",
    status: BLOG_STATUS.DRAFT,
  });

  const [tagInput, setTagInput] = useState("");
  const [availableAuthors, setAvailableAuthors] = useState([]);
  const [authorFormOpen, setAuthorFormOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isUploadingFeatured, setIsUploadingFeatured] = useState(false);
  const [editType, setEditType] = useState("markdown");
  const [contentMarkdown, setContentMarkdown] = useState("");

  useEffect(() => {
    if (blogPost) {
      setFormData({
        title: blogPost.title || "",
        excerpt: blogPost.excerpt || "",
        content: blogPost.content || "",
        featuredImage: blogPost.featuredImage || "",
        author: blogPost.author?._id || blogPost.author || "",
        category: blogPost.category || "",
        tags: blogPost.tags || [],
        seoTitle: blogPost.seoTitle || "",
        seoDescription: blogPost.seoDescription || "",
        status: blogPost.status || BLOG_STATUS.DRAFT,
      });
      setEditType("wysiwyg");
      setContentMarkdown("");
    } else {
      setFormData({
        title: "",
        excerpt: "",
        content: "",
        featuredImage: "",
        author: "",
        category: "",
        tags: [],
        seoTitle: "",
        seoDescription: "",
        status: BLOG_STATUS.DRAFT,
      });
      setEditType("markdown");
      setContentMarkdown("");
    }
  }, [blogPost, open]);

  // Fetch available authors on component mount
  useEffect(() => {
    const loadAuthors = async () => {
      try {
        const response = await fetchAvailableAuthors();
        if (response.success) {
          setAvailableAuthors(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch authors:", error);
        setSnackbar({
          open: true,
          message: "Failed to load available authors",
          severity: "error",
        });
      }
    };

    loadAuthors();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAuthorChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      author: { ...prev.author, [field]: value },
    }));
  };

  const uploadFileToFilesAPI = async (file) => {
    const user = JSON.parse(localStorage.getItem("user"));

    const form = new FormData();
    form.append("ownerId", user._id);
    form.append("type", "file");
    form.append("file", file);
    form.append("ownerEmail", user.email);

    const uploadUrl = `https://api.files.clikkle.com:4500/api/fs/create?id=${user._id}`;
    const resp = await axios.post(uploadUrl, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const fileId = resp?.data?.record?._id;
    if (!fileId) {
      throw new Error("Upload failed: file id missing");
    }
    return `https://api.files.clikkle.com:4500/api/fs/access/${fileId}?userId=${user._id}`;
  };

  const handleFeaturedImageFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingFeatured(true);
    try {
      const accessUrl = await uploadFileToFilesAPI(file);
      console.log("accessUrl");
      console.log(accessUrl);

      setFormData((prev) => ({ ...prev, featuredImage: accessUrl }));
    } catch (err) {
      console.error("Featured image upload error:", err);
      setSnackbar({
        open: true,
        message: "Failed to upload image",
        severity: "error",
      });
    } finally {
      setIsUploadingFeatured(false);
    }
  };

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleTagAdd();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{
        backgroundColor: "transparent",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "none",
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditing ? "Edit Blog Post" : "Create New Blog Post"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Blog Title"
                value={formData.title}
                onChange={handleChange("title")}
                required
                helperText="This will be used to generate the URL slug"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Excerpt"
                value={formData.excerpt}
                onChange={handleChange("excerpt")}
                required
                multiline
                rows={2}
                helperText="A brief summary of the blog post"
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel>Editor Mode</InputLabel>
                  <Select
                    value={editType}
                    label="Editor Mode"
                    onChange={(e) => setEditType(e.target.value)}
                  >
                    <MenuItem value="markdown">Markdown</MenuItem>
                    <MenuItem value="wysiwyg">WYSIWYG</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {editType === "markdown" ? (
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  <MDEditor
                    value={contentMarkdown}
                    onChange={(v) => {
                      const next = v || "";
                      setContentMarkdown(next);
                      const html = DOMPurify.sanitize(marked.parse(next));
                      setFormData((prev) => ({ ...prev, content: html }));
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(html) =>
                      setFormData((prev) => ({ ...prev, content: html || "" }))
                    }
                  />
                </Box>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                The main content of your blog post
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Featured Image URL"
                value={formData.featuredImage}
                onChange={handleChange("featuredImage")}
                placeholder="https://example.com/image.jpg"
                helperText="URL for the main blog image"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                component="label"
                disabled={isUploadingFeatured}
              >
                {isUploadingFeatured ? "Uploading..." : "Upload Featured Image"}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFeaturedImageFileSelect}
                />
              </Button>
            </Grid>

            {formData.featuredImage && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <img
                    src={formData.featuredImage}
                    alt="Featured Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 240,
                      borderRadius: 8,
                    }}
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Select Author</InputLabel>
                  {console.log(formData)}
                  <Select
                    value={formData.author || ""}
                    onChange={(event) => {
                      const selectedAuthorId = event.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        author: selectedAuthorId,
                      }));
                    }}
                    required
                  >
                    <MenuItem value="">
                      <em>Select an author</em>
                    </MenuItem>
                    {availableAuthors.map((author) => (
                      <MenuItem key={author?.id} value={author?.id}>
                        {author?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setAuthorFormOpen(true)}
                  sx={{ minWidth: "auto", whiteSpace: "nowrap" }}
                >
                  New Author
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleChange("category")}
                  required
                >
                  {blogCategories
                    .filter((cat) => cat !== "All")
                    .map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleChange("status")}
                >
                  <MenuItem value={BLOG_STATUS.DRAFT}>Draft</MenuItem>
                  <MenuItem value={BLOG_STATUS.PUBLISHED}>Published</MenuItem>
                  <MenuItem value={BLOG_STATUS.ARCHIVED}>Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <TextField
                  fullWidth
                  label="Keywords"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleTagInputKeyPress}
                  helperText="Press Enter to add Keywords"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTagAdd} edge="end">
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}
                >
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleTagRemove(tag)}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isUploadingFeatured}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isUploadingFeatured}
          >
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>

      {/* Author Creation Form */}
      <AuthorForm
        open={authorFormOpen}
        onClose={() => setAuthorFormOpen(false)}
        onSubmit={async (authorData) => {
          try {
            const response = await createAuthor(authorData);
            if (response.success) {
              // Refresh the authors list
              const authorsResponse = await fetchAvailableAuthors();
              if (authorsResponse.success) {
                setAvailableAuthors(authorsResponse.data);
                setSnackbar({
                  open: true,
                  message: "Author created successfully",
                  severity: "success",
                });
              }
            }
          } catch (error) {
            console.error("Error creating author:", error);
            setSnackbar({
              open: true,
              message: "Failed to create author",
              severity: "error",
            });
          }
        }}
      />
    </Dialog>
  );
};

// Main Blogs Component
const Blogs = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  // Authors management state
  // Define missing state variables in the Blogs component
  const [showAuthorsSection, setShowAuthorsSection] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [authorsDialogOpen, setAuthorsDialogOpen] = useState(false);

  // Blog posts state for drag and drop
  const [blogPosts, setBlogPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    status: "",
    sortBy: "orderIndex",
    sortOrder: "asc",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Handle direct actions from Navbar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");
    if (action === "new") {
      handleCreate();
    } else if (action === "newAuthor") {
      setEditingAuthor(null);
      setAuthorsDialogOpen(true);
    }
  }, [location.search]);

  // Fetch blog posts
  const {
    data: blogsData,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["blogs", page, limit, filters],
    queryFn: () => fetchBlogPosts(page, limit, filters),
    keepPreviousData: true,
  });

  // Fetch blog stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["blogStats"],
    queryFn: fetchBlogStats,
  });

  // Fetch all authors
  const { data: authorsData, refetch: refetchAuthors } = useQuery({
    queryKey: ["authors"],
    queryFn: fetchAllAuthors,
    enabled: showAuthorsSection,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createBlogPost,
    onSuccess: () => {
      queryClient.invalidateQueries(["blogs"]);
      queryClient.invalidateQueries(["blogStats"]);
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: "Blog post created successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error creating blog post: ${
          error.response?.data?.message || error.message
        }`,
        severity: "error",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateBlogPost(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["blogs"]);
      queryClient.invalidateQueries(["blogStats"]);
      setDialogOpen(false);
      setEditingBlog(null);
      setSnackbar({
        open: true,
        message: "Blog post updated successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating blog post: ${
          error.response?.data?.message || error.message
        }`,
        severity: "error",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteBlogPost,
    onSuccess: () => {
      queryClient.invalidateQueries(["blogs"]);
      queryClient.invalidateQueries(["blogStats"]);
      setSnackbar({
        open: true,
        message: "Blog post deleted successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting blog post: ${
          error.response?.data?.message || error.message
        }`,
        severity: "error",
      });
    },
  });

  // Status mutation
  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => updateBlogStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(["blogs"]);
      queryClient.invalidateQueries(["blogStats"]);
      setSnackbar({
        open: true,
        message: "Blog post status updated!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating status: ${
          error.response?.data?.message || error.message
        }`,
        severity: "error",
      });
    },
  });

  // Reorder mutation
  const reorderBlogsMutation = useMutation({
    mutationFn: reorderBlogs,
    onSuccess: () => {
      queryClient.invalidateQueries(["blogs"]);
      setSnackbar({
        open: true,
        message: "Blog order updated successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      console.error("Reorder mutation error:", error);
      console.error("Error response:", error.response?.data);
      setSnackbar({
        open: true,
        message: `Error updating blog order: ${
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message
        }`,
        severity: "error",
      });
    },
  });

  // Authors management mutations
  const updateAuthorMutation = useMutation({
    mutationFn: ({ id, data }) => updateAuthor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["blogs"]);
      queryClient.invalidateQueries(["blogStats"]);
      refetchAuthors();
      setEditingAuthor(null);
      setSnackbar({
        open: true,
        message: "Author updated successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating author: ${
          error.response?.data?.message || error.message
        }`,
        severity: "error",
      });
    },
  });

  const deleteAuthorMutation = useMutation({
    mutationFn: deleteAuthor,
    onSuccess: () => {
      queryClient.invalidateQueries(["blogs"]);
      queryClient.invalidateQueries(["blogStats"]);
      refetchAuthors();
      setSnackbar({
        open: true,
        message: "Author deleted successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting author: ${
          error.response?.data?.message || error.message
        }`,
        severity: "error",
      });
    },
  });

  const handleCreate = () => {
    setEditingBlog(null);
    setDialogOpen(true);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setDialogOpen(true);
  };
  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "", // "blog" or "author"
    id: null,
  });
  const handleDelete = (id) => {
    setConfirmationDialog({
      open: true,
      title: "Confirm Delete",
      message: "Are you sure you want to delete this blog post?",
      onConfirm: () => deleteMutation.mutate(id),
      type: "blog",
      id: id,
    });
  };

  // Handle drag and drop reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // If dropped in the same position, do nothing
    if (source.index === destination.index) return;

    // Create a new array with the reordered items
    const items = Array.from(blogPosts);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    // Update the local state immediately for smooth UX
    setBlogPosts(items);

    // Extract the IDs in the new order - only include valid MongoDB ObjectIds
    const blogIds = items
      .map((item) => item._id)
      .filter((id) => id != null && typeof id === "string" && id.length === 24); // MongoDB ObjectId is 24 characters

    // Debug: Log the blog IDs being sent
    console.log("Reordering blogs with IDs:", blogIds);
    console.log("Blog items:", items);
    console.log(
      "Items with null/undefined IDs:",
      items.filter((item) => item._id == null)
    );
    console.log(
      "Items with invalid ID format:",
      items.filter(
        (item) =>
          item._id != null &&
          (typeof item._id !== "string" || item._id.length !== 24)
      )
    );

    // Only call API if we have valid IDs
    if (blogIds.length === 0) {
      console.error("No valid blog IDs found for reordering");
      setSnackbar({
        open: true,
        message: "Error: No valid blog IDs found for reordering",
        severity: "error",
      });
      return;
    }

    // Call the reorder API
    reorderBlogsMutation.mutate(blogIds);
  };

  const handleStatusChange = (id, currentStatus) => {
    let newStatus;
    switch (currentStatus) {
      case BLOG_STATUS.DRAFT:
        newStatus = BLOG_STATUS.PUBLISHED;
        break;
      case BLOG_STATUS.PUBLISHED:
        newStatus = BLOG_STATUS.ARCHIVED;
        break;
      case BLOG_STATUS.ARCHIVED:
        newStatus = BLOG_STATUS.DRAFT;
        break;
      default:
        newStatus = BLOG_STATUS.DRAFT;
    }

    statusMutation.mutate({ id, status: newStatus });
  };

  const handleSubmit = (formData) => {
    if (editingBlog) {
      updateMutation.mutate({ id: editingBlog?._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleSearch = (event) => {
    setFilters((prev) => ({ ...prev, search: event.target.value }));
    setPage(1);
  };

  const handleCategoryFilter = (event) => {
    setFilters((prev) => ({ ...prev, category: event.target.value }));
    setPage(1);
  };

  const handleStatusFilter = (event) => {
    setFilters((prev) => ({ ...prev, status: event.target.value }));
    setPage(1);
  };

  const handleSort = (sortBy) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === "desc" ? "asc" : "desc",
    }));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialog({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
      type: "",
      id: null,
    });
  };

  const handleConfirmAction = () => {
    if (confirmationDialog.onConfirm) {
      confirmationDialog.onConfirm();
    }
    handleCloseConfirmationDialog();
  };

  // Authors management handlers
  const handleEditAuthor = (author) => {
    setEditingAuthor(author);
    setAuthorsDialogOpen(true);
  };

  const handleDeleteAuthor = (id) => {
    setConfirmationDialog({
      open: true,
      title: "Confirm Delete",
      message: "Are you sure you want to delete this author?",
      onConfirm: () => deleteAuthorMutation.mutate(id),
      type: "author",
      id: id,
    });
  };

  const handleAuthorSubmit = (authorData) => {
    if (editingAuthor) {
      updateAuthorMutation.mutate({ id: editingAuthor?.id, data: authorData });
    } else {
      createAuthor(authorData)
        .then((response) => {
          if (response.success) {
            queryClient.invalidateQueries(["blogs"]);
            queryClient.invalidateQueries(["blogStats"]);
            setAuthorsDialogOpen(false);
            setSnackbar({
              open: true,
              message: "Author created successfully!",
              severity: "success",
            });
          }
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: `Error creating author: ${
              error.response?.data?.message || error.message
            }`,
            severity: "error",
          });
        });
    }
  };

  // Helper functions for status display
  const getStatusIcon = (status) => {
    switch (status) {
      case "published":
        return <CheckCircleIcon />;
      case "draft":
        return <EditNoteIcon />;
      case "archived":
        return <ArchiveIcon />;
      default:
        return <HelpIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "published":
        return "success";
      case "draft":
        return "warning";
      case "archived":
        return "default";
      default:
        return "default";
    }
  };

  const getNextStatus = (currentStatus) => {
    switch (currentStatus) {
      case "draft":
        return "published";
      case "published":
        return "archived";
      case "archived":
        return "draft";
      default:
        return "draft";
    }
  };

  // Update blogPosts state when data changes
  useEffect(() => {
    if (blogsData?.data) {
      setBlogPosts(blogsData.data);
    }
  }, [blogsData?.data]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading blog posts: {error.message}
      </Alert>
    );
  }

  const pagination = blogsData?.pagination || {};

  return (
    <Box
      sx={{
        p: 3,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(4),
        borderRadius: 2,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(4),
          borderRadius: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Blog Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage blog posts for Clikkle Website
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          size={isMobile ? "small" : "medium"}
        >
          New Blog Post
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <MdOutlineArticle color="primary" />
                <Box>
                  <Typography variant="h6">
                    {statsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      statsData?.data?.total || 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Posts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <GrFormView color="success" />
                <Box>
                  <Typography variant="h6">
                    {statsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      statsData?.data?.published || 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Published
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <MdOutlineVisibilityOff color="warning" />
                <Box>
                  <Typography variant="h6">
                    {statsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      statsData?.data?.draft || 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drafts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AnalyticsIcon color="info" />
                <Box>
                  <Typography variant="h6">
                    {statsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      statsData?.data?.totalViews || 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Views
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Authors Management Section */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          onClick={() => setShowAuthorsSection(!showAuthorsSection)}
          startIcon={
            showAuthorsSection ? <MdOutlineVisibilityOff /> : <GrFormView />
          }
          sx={{ mb: 2 }}
        >
          {showAuthorsSection ? "Hide Authors" : "Manage Authors"}
        </Button>

        {showAuthorsSection && (
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Authors Management</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingAuthor(null);
                    setAuthorsDialogOpen(true);
                  }}
                  size="small"
                >
                  New Author
                </Button>
              </Box>

              <Box sx={{ mt: 2 }}>
                {authorsData?.data ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Avatar</TableCell>
                          <TableCell>Full Name</TableCell>
                          <TableCell>Bio</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {authorsData.data.map((author) => (
                          <TableRow key={author?.id}>
                            <TableCell>
                              <Avatar
                                src={author?.avatar || ""}
                                sx={{ width: 40, height: 40 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" fontWeight="medium">
                                {author?.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                maxWidth={"300px"}
                              >
                                {author?.bio || "No bio provided"}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditAuthor(author)}
                                    color="primary"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteAuthor(author?.id)
                                    }
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No authors found. Click "New Author" to create one.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Filters Section */}
      <Card
        sx={{
          backgroundColor: "transparent",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
          mb: 3,
        }}
      >
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent={"center"}
          >
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                placeholder="Search blog posts..."
                value={filters.search}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={handleCategoryFilter}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {blogCategories
                    .filter((cat) => cat !== "All")
                    .map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleStatusFilter}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value={BLOG_STATUS.DRAFT}>Draft</MenuItem>
                  <MenuItem value={BLOG_STATUS.PUBLISHED}>Published</MenuItem>
                  <MenuItem value={BLOG_STATUS.ARCHIVED}>Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortBy}
                  onChange={(e) => handleSort(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="orderIndex">Manual Order</MenuItem>
                  <MenuItem value="createdAt">Created Date</MenuItem>
                  <MenuItem value="publishedDate">Published Date</MenuItem>
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="views">Views</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Rows per page:
                </Typography>
                <Select
                  value={limit}
                  onChange={(e) =>
                    handleRowsPerPageChange(Number(e.target.value))
                  }
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Blog Posts Table */}
      <Card
        sx={{
          backgroundColor: "transparent",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {filters?.sortBy === "orderIndex" ? (
                  <TableCell>Order</TableCell>
                ) : (
                  ""
                )}
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Published Date</TableCell>
                <TableCell>Views</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="blogs-table">
                {(provided) => (
                  <TableBody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {blogPosts.map((blog, index) => {
                      // Use _id if available, otherwise use index as fallback
                      const blogId = blog?._id || `blog-${index}`;
                      return (
                        <Draggable
                          key={blogId}
                          draggableId={blogId}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                backgroundColor: snapshot.isDragging
                                  ? "action.hover"
                                  : "inherit",
                              }}
                            >
                              {filters?.sortBy === "orderIndex" ? (
                                <TableCell width={50} align="center">
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{
                                      cursor: "grab",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      "&:hover": {
                                        color: "primary.main",
                                      },
                                    }}
                                  >
                                    <DragHandleIcon fontSize="small" />
                                    <Typography
                                      variant="caption"
                                      sx={{ ml: 0.5 }}
                                    >
                                      {index + 1}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              ) : (
                                ""
                              )}
                              <TableCell>
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {blog?.title}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    /{blog?.slug}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Avatar
                                    src={blog?.author?.avatar || ""}
                                    sx={{ width: 32, height: 32 }}
                                  />
                                  <Typography variant="body2">
                                    {blog?.author?.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={blog?.category}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={getStatusIcon(blog?.status)}
                                  label={blog?.status}
                                  color={getStatusColor(blog?.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {new Date(
                                    blog?.publishedDate
                                  ).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {blog?.views || 0}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "center",
                                  }}
                                >
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEdit(blog)}
                                      color="primary"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Change Status">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleStatusChange(
                                          blog?._id,
                                          blog?.status
                                        )
                                      }
                                      color="secondary"
                                    >
                                      {getStatusIcon(blog?.status)}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(blog?._id)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {(pagination.pages > 1 ||
          pagination.totalPages > 1 ||
          pagination.pageCount > 1 ||
          (pagination.total && pagination.total > pagination.limit)) && (
          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <Pagination
              count={
                pagination.pages ||
                pagination.totalPages ||
                pagination.pageCount ||
                Math.ceil((pagination.total || 0) / (pagination.limit || limit))
              }
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Loading Overlay */}
        {isFetching && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Card>

      {/* Blog Form Modal */}
      <BlogForm
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingBlog(null);
        }}
        blogPost={editingBlog}
        onSubmit={handleSubmit}
        isEditing={!!editingBlog}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Empty State */}
      {blogPosts.length === 0 && !isLoading && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <MdOutlineArticle
            sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No blog posts found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {filters.search || filters.category || filters.status
              ? "Try adjusting your search criteria"
              : "Get started by creating your first blog post"}
          </Typography>
          {!filters.search && !filters.category && !filters.status && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Create Blog Post
            </Button>
          )}
        </Box>
      )}

      {/* Author Form Modal */}
      <AuthorForm
        open={authorsDialogOpen}
        onClose={() => {
          setAuthorsDialogOpen(false);
          setEditingAuthor(null);
        }}
        onSubmit={handleAuthorSubmit}
        editData={editingAuthor}
        isEditing={!!editingAuthor}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialog.open}
        onClose={handleCloseConfirmationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmationDialog.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{confirmationDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmationDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color="error"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Blogs;
